import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { sanitizeChatContent, extractArtifactPayload } from '@/utils/sanitizeChatContent';
import { Plus, History, Trash2 } from 'lucide-react';
import { LEGAL_STRINGS } from '../../../constants/legalStrings';
import { getEmmaPageState } from '../../../utils/emmaPageState';
import { withApiAuthHeaders } from '../../../utils/authHeaders';
import { apiRequest } from '../../../lib/api';
import { buildApiUrl } from '../../../lib/apiBase';
import { useEmmaContext } from '../../../contexts/EmmaContext';
import { useEmmaActionExecutor } from '../../../hooks/emma/useEmmaActionExecutor';
import { useEmmaWebSocket } from '../../../hooks/emma/useEmmaWebSocket';
import emmaSessionApi from '../../../services/emma/emmaSessionApi';

interface ExpandedViewProps {
  onCollapse?: () => void;
}

interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'emma';
  timestamp: number;
  thinkingContent?: string;
  isChunk?: boolean;
  isComplete?: boolean;
}

interface EmmaSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  timestamp: number;
  backendKey?: string;
}

const renderWithCaret = (children: React.ReactNode) => {
  const replaceInText = (text: string) => {
    if (!text.includes('||cursor||')) return text;
    const parts = text.split('||cursor||');
    return (
      <>
        {parts[0]}
        <span className="inline-block animate-pulse text-emma-500 font-bold ml-0.5 align-middle">▊</span>
        {parts[1]}
      </>
    );
  };

  if (typeof children === 'string') {
    return replaceInText(children);
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        return <React.Fragment key={index}>{replaceInText(child)}</React.Fragment>;
      }
      return child;
    });
  }

  return children;
};

interface ExpandedBubbleProps {
  msg: ChatMessage;
  isStreaming: boolean;
}

const ExpandedBubble: React.FC<ExpandedBubbleProps> = ({ msg, isStreaming }) => {
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(true);

  useEffect(() => {
    if (msg.content) {
      setIsThinkingExpanded(false);
    }
  }, [msg.content]);

  return (
    <div className={`emma-bubble ${msg.sender === 'emma' ? 'emma-bubble-ai' : 'emma-bubble-user'}`}>
      {msg.sender === 'emma' && (
        <div className="emma-bubble-avatar">E</div>
      )}
      <div className="emma-bubble-content">
        {msg.sender === 'emma' ? (
          <>
            {msg.thinkingContent && (() => {
              const steps = msg.thinkingContent.split('\n').map(s => s.trim()).filter(Boolean);
              const isActive = !msg.isComplete;
              const lastStep = steps[steps.length - 1] || '';
              const lastMatch = lastStep.match(/^([\uD800-\uDBFF][\uDC00-\uDFFF]|\p{Emoji_Presentation}|\p{Emoji}\uFE0F?)/u);
              const lastText = lastMatch ? lastStep.substring(lastMatch[0].length).trim() : lastStep.trim();
              const emoji = lastMatch ? lastMatch[0] : '⚡';
              if (!isActive) return null;
              return (
                <div className="emma-thinking-card">
                  <div className="emma-thinking-wave">
                    <span className="wave-dot"></span>
                    <span className="wave-dot"></span>
                    <span className="wave-dot"></span>
                  </div>
                  <span className="emma-thinking-step-bullet">{emoji}</span>
                  <span className="emma-thinking-active-text">{lastText}...</span>
                  <div className="emma-thinking-shimmer" />
                </div>
              );
            })()}
            {msg.content ? (
              <ReactMarkdown
                components={{
                  p: ({ children, ...props }) => <p className="emma-md-p" {...props}>{renderWithCaret(children)}</p>,
                  ul: ({ children, ...props }) => <ul className="emma-md-ul" {...props}>{children}</ul>,
                  ol: ({ children, ...props }) => <ol className="emma-md-ol" {...props}>{children}</ol>,
                  li: ({ children, ...props }) => <li className="emma-md-li" {...props}>{renderWithCaret(children)}</li>,
                  strong: ({ children, ...props }) => <strong className="emma-md-strong" {...props}>{children}</strong>,
                  a: ({ href, children, ...props }) => (
                    <a
                      {...props}
                      href={href}
                      onClick={(e) => {
                        e.preventDefault();
                        const linkText = String(children || '');
                        if (
                          linkText.includes('Open Template Preview') ||
                          linkText.includes('View Code') ||
                          (href && href.includes('/api/templates/preview/'))
                        ) {
                          const artifactPayload = extractArtifactPayload(msg);
                          window.dispatchEvent(new CustomEvent('emma-open-artifact-panel', {
                            detail: artifactPayload
                          }));
                        } else if (href) {
                          const target = href.startsWith('/api/') ? buildApiUrl(href) : href;
                          window.open(target, '_blank');
                        }
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="emma-md-a underline font-semibold text-blue-600 dark:text-blue-400 hover:opacity-80 transition-opacity"
                    >
                      {renderWithCaret(children)}
                    </a>
                  ),
                }}
              >
                {sanitizeChatContent(isStreaming ? msg.content + ' ||cursor||' : msg.content)}
              </ReactMarkdown>
            ) : null}
          </>
        ) : (
          <p className="emma-md-p">{msg.content}</p>
        )}
        {(() => {
          const isAssetMessage = msg.sender === 'emma' && (
            Boolean(msg.metadata?.artifact) ||
            Boolean(msg.metadata?.ui_action) ||
            Boolean(msg.metadata?.isStatelessAsset) ||
            Boolean(msg.metadata?.statelessAsset) ||
            Boolean(msg.metadata?.assetType) ||
            (typeof msg.content === 'string' && (
              msg.content.includes('Asset Generated') ||
              msg.content.includes('Autonomous Asset Generation') ||
              msg.content.includes('Document Generated') ||
              msg.content.includes('Open Template Preview') ||
              msg.content.includes('Template Content Updated') ||
              msg.content.includes('Styling Updated') ||
              msg.content.includes('Poster') ||
              msg.content.includes('Battlecard')
            ))
          );
          return isAssetMessage ? (
            <div className="mt-3 pt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  const artifactPayload = extractArtifactPayload(msg);
                  window.dispatchEvent(new CustomEvent('emma-open-artifact-panel', {
                    detail: artifactPayload
                  }));
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium text-sm flex items-center gap-2 shadow-md transition-all cursor-pointer hover:bg-emerald-700 active:scale-95"
              >
                <span>📄</span> Open Artifact Panel
              </button>
              <button
                onClick={() => {
                  const artifactPayload = extractArtifactPayload(msg);
                  const previewUrl = artifactPayload?.previewUrl || (artifactPayload?.slug ? `/api/templates/preview/${artifactPayload.slug}` : null);
                  if (previewUrl) {
                    const fullUrl = previewUrl.startsWith('/api/') ? buildApiUrl(previewUrl) : previewUrl;
                    window.open(fullUrl, '_blank');
                    return;
                  }
                  if (artifactPayload?.content && artifactPayload.content.includes('<')) {
                    const blob = new Blob([artifactPayload.content], { type: 'text/html;charset=utf-8' });
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                  }
                }}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg font-medium text-sm flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                title="Open artifact in full browser tab"
              >
                <span>🌐</span> Open in New Tab
              </button>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

export const ExpandedView: React.FC<ExpandedViewProps> = ({ onCollapse }) => {
  const { pageContext } = useEmmaContext();
  const { executeActions } = useEmmaActionExecutor();
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Safety timeout: automatically reset isTyping if response is delayed > 20s
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isTyping) {
      timer = setTimeout(() => {
        setIsTyping(false);
      }, 20000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isTyping]);
  
  const [sessions, setSessions] = useState<EmmaSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const sessionsRef = React.useRef(sessions);
  React.useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  // Keep a ref of the active session ID so the lastMessage effect can read
  // the latest value without having activeSessionId in its dependency array.
  // This prevents session-switches from re-triggering the effect with a
  // stale lastMessage and polluting the new chat with old messages.
  const activeSessionIdRef = React.useRef(activeSessionId);
  React.useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  // A counter that increments whenever the active session changes.
  // The lastMessage effect captures it at invocation time; if it has advanced
  // by the time we apply the update, the message belongs to the old session
  // and is silently dropped — without blocking streaming chunks.
  const sessionEpochRef = React.useRef(0);
  
  const { 
    isConnected, 
    connectionStatus, 
    sendMessage, 
    lastMessage, 
    typingIndicator,
    error: wsError,
    reconnect 
  } = useEmmaWebSocket({ 
    sessionId: activeSessionId || undefined,
    autoReconnect: true,
    maxReconnectAttempts: 1000
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // V6: Page-aware greeting generator
  const getPageGreeting = (pageLabel?: string): string => {
    const greetings: Record<string, string> = {
      'Partners': "Hi! I'm **Emma**, your AI marketing intelligence partner.\n\nI can help you manage partner invitations, review questionnaire responses, and optimize your partner activation funnel.\n\nWhat would you like to tackle today?",
      'Partner Dashboard': "Hi! I'm **Emma**, your AI marketing intelligence partner.\n\nYour questionnaire is live! I can help you interpret responses, refine your answers, or prepare for campaign generation.\n\nWhat would you like to tackle today?",
      'Campaigns': "Hi! I'm **Emma**, your AI marketing intelligence partner.\n\nReady to generate content? I can help with campaign strategy, messaging frameworks, and content optimization.\n\nWhat would you like to tackle today?",
    };
    return greetings[pageLabel || ''] || "Hi! I'm **Emma**, your AI marketing intelligence partner.\n\nI can help with campaign strategy, content creation, market intelligence, and workflow automation.\n\nWhat would you like to tackle today?";
  };

  // Initialize and load sessions from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('emma_chat_sessions');
    const activeId = localStorage.getItem('emma_active_session_id');
    
    let loadedSessions: EmmaSession[] = [];
    if (stored) {
      try {
        loadedSessions = JSON.parse(stored);
      } catch (e) {
        console.warn('Failed to parse emma_chat_sessions:', e);
      }
    }
    
    if (loadedSessions.length === 0) {
      const welcomeSession: EmmaSession = {
        id: `session_${Date.now()}`,
        title: 'Welcome Chat',
        messages: [
          {
            id: 'welcome',
            content: getPageGreeting(pageContext.pageLabel),
            sender: 'emma',
            timestamp: Date.now()
          }
        ],
        timestamp: Date.now()
      };
      loadedSessions = [welcomeSession];
      localStorage.setItem('emma_chat_sessions', JSON.stringify(loadedSessions));
      localStorage.setItem('emma_active_session_id', welcomeSession.id);
      localStorage.setItem('emma-session-id', welcomeSession.id);
      
      setSessions(loadedSessions);
      setActiveSessionId(welcomeSession.id);
      setMessages(welcomeSession.messages);
    } else {
      // V6: Update existing session's welcome message with page-aware greeting
      const firstMsg = loadedSessions[0]?.messages?.[0];
      if (firstMsg && (firstMsg.id === 'welcome' || firstMsg.id.startsWith('welcome-'))) {
        const updatedSession = { ...loadedSessions[0] };
        updatedSession.messages = [...updatedSession.messages];
        updatedSession.messages[0] = {
          ...updatedSession.messages[0],
          content: getPageGreeting(pageContext.pageLabel),
        };
        loadedSessions[0] = updatedSession;
        localStorage.setItem('emma_chat_sessions', JSON.stringify(loadedSessions));
      }
      
      setSessions(loadedSessions);
      const active = loadedSessions.find(s => s.id === activeId) || loadedSessions[0];
      setActiveSessionId(active.id);
      setMessages(active.messages);
      localStorage.setItem('emma_active_session_id', active.id);
      localStorage.setItem('emma-session-id', active.id);
    }
  }, []);

  // Substrate awareness greeting trigger
  useEffect(() => {
    if (!pageContext.campaignId) return;
    
    const fetchCampaignSummary = async () => {
      try {
        const response = await apiRequest(`/api/v1/emma/campaign-summary/${pageContext.campaignId}`);
        if (response.ok) {
          const summary = await response.json();
          const docCount = summary.document_count;
          const personaCount = summary.persona_count;
          const messagingStatus = summary.messaging_completeness === 'complete' ? 'fully loaded' : 'partially loaded';
          
          const summaryGreeting = `Hi! I'm **Emma**, your AI marketing intelligence partner.\n\nI've analyzed the substrate for **${summary.campaign_name}**:\n* 📚 **${docCount}** campaign documents ingested.\n* 👥 **${personaCount}** validated client personas found.\n* 💬 Messaging strategy is **${messagingStatus}**.\n\nWhat would you like to build or customize for this campaign today?`;
          
          setMessages(prev => {
            if (prev.length > 0 && (prev[0].id === 'welcome' || prev[0].id.startsWith('welcome-'))) {
              const updated = [...prev];
              updated[0] = {
                ...updated[0],
                content: summaryGreeting
              };
              return updated;
            }
            return prev;
          });
        }
      } catch (err) {
        console.warn('Failed to fetch campaign summary for substrate awareness:', err);
      }
    };
    
    fetchCampaignSummary();
  }, [pageContext.campaignId]);

  // V6: Update greeting when page context changes
  useEffect(() => {
    if (!pageContext.pageLabel) return;
    
    setMessages(prev => {
      if (prev.length > 0 && (prev[0].id === 'welcome' || prev[0].id.startsWith('welcome-'))) {
        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          content: getPageGreeting(pageContext.pageLabel),
        };
        return updated;
      }
      return prev;
    });
  }, [pageContext.pageLabel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const isDuplicateEmmaMessage = (prev: ChatMessage[], candidate: ChatMessage): boolean => {
    if (!candidate || candidate.sender !== 'emma') return false;
    if (prev.some((m) => m.id === candidate.id)) return true;
    const lastEmma = [...prev].reverse().find((m) => m.sender === 'emma');
    if (!lastEmma) return false;
    const sameText = (lastEmma.content || '').trim() === (candidate.content || '').trim();
    if (!sameText) return false;
    return Math.abs(Number(candidate.timestamp || 0) - Number(lastEmma.timestamp || 0)) <= 5000;
  };

  const syncMessageToBackend = async (session: EmmaSession, role: 'user' | 'emma', content: string) => {
    if (!emmaSessionApi.isBackendSessionEnabled()) return;
    
    let key = session.backendKey;
    if (!key) {
      try {
        const deviceId = localStorage.getItem('emma_device_id') || `device_${Date.now()}`;
        const res = await emmaSessionApi.createSession(deviceId, 'desktop');
        if (res.success && res.data) {
          key = res.data.session_key;
          session.backendKey = key;
          
          setSessions(prev => {
            const updated = prev.map(s => s.id === session.id ? { ...s, backendKey: key } : s);
            localStorage.setItem('emma_chat_sessions', JSON.stringify(updated));
            return updated;
          });
        }
      } catch (err) {
        console.warn('Failed to pre-create backend session:', err);
      }
    }
    
    if (key) {
      try {
        await emmaSessionApi.saveMessage(key, role, content);
      } catch (err) {
        console.warn('Failed to sync message to backend:', err);
      }
    }
  };

  const updateSessionMessages = (sessionId: string, newMessages: ChatMessage[]) => {
    setSessions(prev => {
      const updated = prev.map(s => {
        if (s.id === sessionId) {
          let title = s.title;
          if (title === 'Welcome Chat' || title === 'New Chat') {
            const firstUser = newMessages.find(m => m.sender === 'user');
            if (firstUser) {
              const snippet = firstUser.content.slice(0, 24);
              title = snippet.length < firstUser.content.length ? `${snippet}...` : snippet;
            }
          }
          return {
            ...s,
            title,
            messages: newMessages,
            timestamp: Date.now()
          };
        }
        return s;
      });
      localStorage.setItem('emma_chat_sessions', JSON.stringify(updated));
      return updated;
    });
  };

  const handleNewChat = async () => {
    const newId = `session_${Date.now()}`;
    const newSession: EmmaSession = {
      id: newId,
      title: 'New Chat',
      messages: [
        {
          id: `welcome-${Date.now()}`,
          content: getPageGreeting(pageContext.pageLabel),
          sender: 'emma',
          timestamp: Date.now()
        }
      ],
      timestamp: Date.now()
    };

    if (emmaSessionApi.isBackendSessionEnabled()) {
      try {
        const deviceId = localStorage.getItem('emma_device_id') || `device_${Date.now()}`;
        const res = await emmaSessionApi.createSession(deviceId, 'desktop');
        if (res.success && res.data) {
          newSession.backendKey = res.data.session_key;
        }
      } catch (err) {
        console.warn('Failed to pre-create backend session:', err);
      }
    }

    const updatedSessions = [newSession, ...sessions];
    setSessions(updatedSessions);
    setActiveSessionId(newId);
    setMessages(newSession.messages);
    setIsTyping(false);
    // Advance the epoch so any in-flight setMessages from the OLD session
    // detects the change and drops its stale update.
    sessionEpochRef.current += 1;
    localStorage.setItem('emma_chat_sessions', JSON.stringify(updatedSessions));
    localStorage.setItem('emma_active_session_id', newId);
    localStorage.setItem('emma-session-id', newId);
    setShowHistory(false);
  };

  const handleSelectSession = (id: string) => {
    const session = sessions.find(s => s.id === id);
    if (session) {
      setActiveSessionId(id);
      setMessages(session.messages);
      setIsTyping(false);
      // Advance epoch to discard any in-flight stale WS messages
      sessionEpochRef.current += 1;
      localStorage.setItem('emma_active_session_id', id);
      localStorage.setItem('emma-session-id', id);
      setShowHistory(false);
    }
  };

  const handleDeleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = sessions.filter(s => s.id !== id);

    if (updated.length === 0) {
      const welcomeSession: EmmaSession = {
        id: `session_${Date.now()}`,
        title: 'Welcome Chat',
        messages: [
          {
            id: 'welcome',
            content: getPageGreeting(pageContext.pageLabel),
            sender: 'emma',
            timestamp: Date.now()
          }
        ],
        timestamp: Date.now()
      };
      updated.push(welcomeSession);
    }

    setSessions(updated);
    localStorage.setItem('emma_chat_sessions', JSON.stringify(updated));

    if (activeSessionId === id) {
      const nextActive = updated[0];
      setActiveSessionId(nextActive.id);
      setMessages(nextActive.messages);
      localStorage.setItem('emma_active_session_id', nextActive.id);
      localStorage.setItem('emma-session-id', nextActive.id);
    }
  };

  useEffect(() => {
    if (!lastMessage) return;

    // Capture the session epoch at effect-invocation time.
    // If the session changes before setMessages runs, the epoch will have
    // advanced and we discard the stale message.
    const epochAtInvocation = sessionEpochRef.current;

    // Use the ref so switching sessions never re-fires this with stale data
    const currentSessionId = activeSessionIdRef.current;

    setMessages(prev => {
      // Guard: session changed between effect invocation and state update
      if (sessionEpochRef.current !== epochAtInvocation) return prev;

      const existingIndex = prev.findIndex(m => m.id === lastMessage.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        const newContent = lastMessage.content || updated[existingIndex].content;
        updated[existingIndex] = {
          ...updated[existingIndex],
          content: newContent,
          thinkingContent: (lastMessage as any).metadata?.thinkingContent || (lastMessage as any).thinkingContent,
          isChunk: (lastMessage as any).isChunk,
          isComplete: (lastMessage as any).isComplete || (lastMessage as any).type === 'message_complete',
        } as any;

        const currentSession = sessionsRef.current.find(s => s.id === currentSessionId);
        if (currentSession && ((lastMessage as any).isComplete || (lastMessage as any).type === 'message_complete')) {
          updateSessionMessages(currentSessionId || '', updated);
          void syncMessageToBackend(currentSession, 'emma', newContent);
        }
        return updated;
      }

      if (isDuplicateEmmaMessage(prev, lastMessage as any)) return prev;

      const newMsgs = [...prev, {
        id: lastMessage.id,
        content: lastMessage.content,
        sender: lastMessage.sender,
        timestamp: lastMessage.timestamp ? new Date(lastMessage.timestamp).getTime() : Date.now(),
        thinkingContent: (lastMessage as any).metadata?.thinkingContent || (lastMessage as any).thinkingContent,
        isChunk: (lastMessage as any).isChunk,
        isComplete: (lastMessage as any).isComplete || (lastMessage as any).type === 'message_complete',
      } as any];

      updateSessionMessages(currentSessionId || '', newMsgs);
      return newMsgs;
    });

    if ((lastMessage as any).type === 'message_complete' || (lastMessage as any).isComplete) {
      setIsTyping(false);
    } else if ((lastMessage as any).isChunk) {
      setIsTyping(false);
    } else if ((lastMessage as any).type === 'typing') {
      setIsTyping(true);
    }
  // ⚠️  Do NOT add activeSessionId here — that would re-run this effect on
  // every session switch and replay the last WS message into the new chat.
  }, [lastMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isTyping || !activeSessionId || !isConnected) return;

    const currentSession = sessions.find(s => s.id === activeSessionId);
    if (!currentSession) return;

    const requestTs = Date.now();
    const userMessage: ChatMessage = {
      id: `user-${requestTs}`,
      content: trimmedInput,
      sender: 'user',
      timestamp: requestTs
    };
    
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setIsTyping(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    updateSessionMessages(activeSessionId, updatedMessages);
    void syncMessageToBackend(currentSession, 'user', trimmedInput);

    sendMessage(trimmedInput);
  };

  return (
    <div
      className="emma-expanded-view"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div className="emma-header">
        <div className="emma-header-left">
          <div className="emma-avatar-dot" />
          <span className="emma-header-title">Emma</span>
          <span className="emma-header-badge">AI Assistant</span>
        </div>
        
        <div className="emma-header-actions">
          <button
            type="button"
            className={`emma-icon-btn ${showHistory ? 'active' : ''}`}
            onClick={() => setShowHistory(!showHistory)}
            title="Chat History"
            aria-label="Chat History"
          >
            <History size={16} />
          </button>
          <button
            type="button"
            className="emma-icon-btn"
            onClick={handleNewChat}
            title="New Chat"
            aria-label="New Chat"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* History Dropdown Pane */}
      {showHistory && (
        <div className="emma-history-dropdown">
          <div className="emma-history-title-bar">
            <span>Recent Chats</span>
            <button
              type="button"
              className="emma-icon-btn"
              style={{ width: 20, height: 20 }}
              onClick={() => setShowHistory(false)}
              aria-label="Close History"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="emma-history-list">
            {sessions.map((s) => (
              <div
                key={s.id}
                className={`emma-history-item ${s.id === activeSessionId ? 'active' : ''}`}
                onClick={() => handleSelectSession(s.id)}
              >
                <div className="emma-history-item-left">
                  <span className="emma-history-item-title">{s.title}</span>
                  <span className="emma-history-item-meta">
                    {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {s.messages.filter(m => m.sender === 'user').length} msgs
                  </span>
                </div>
                <button
                  type="button"
                  className="emma-history-delete-btn"
                  onClick={(e) => handleDeleteSession(s.id, e)}
                  title="Delete Chat"
                  aria-label="Delete Chat"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        className="emma-messages-area"
        style={{
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {messages.map((msg) => {
          const isStreaming = msg.sender === 'emma' && !!msg.isChunk && !msg.isComplete;
          return (
            <ExpandedBubble
              key={msg.id}
              msg={msg}
              isStreaming={isStreaming}
            />
          );
        })}

        {(isTyping || typingIndicator?.isVisible) && (
          <div className="emma-bubble emma-bubble-ai">
            <div className="emma-bubble-avatar">E</div>
            <div className="emma-bubble-content">
              <div className="text-xs text-gray-600 font-medium mb-1.5 leading-snug">
                {typingIndicator?.message || 'Emma is thinking...'}
              </div>
              <div className="emma-typing-indicator">
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="emma-input-area">
        <div className="emma-input-wrapper">
          <textarea
            ref={textareaRef}
            placeholder={isConnected ? "Message Emma..." : "Connecting..."}
            aria-label="Message Emma"
            className="emma-input-field"
            rows={1}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isTyping || !isConnected}
          />
          <button
            type="button"
            onClick={handleSendMessage}
            disabled={isTyping || !inputValue.trim() || !isConnected}
            className="emma-send-btn"
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
